// The product Director's controls and tool forwarding. The live link itself (an ElevenLabs agent over WebRTC) is
// src/directorBridge.tsx, loaded lazily because the SDK carries LiveKit; under VITE_MOCK the mock Director installs
// itself instead. Every client tool forwards to POST /api/director/tool/:name, returns `result` to the agent as a
// string and applies the `ui` directive. v4 (V4-K1): a tool whose ui is play_and_wait's `play` (`wait: true`) is
// answered only when playback settles (src/playwait.ts), with the outcome text instead of `result`.
import { api } from "./api";
import { switchDirectorVoice } from "./chat";
import { appMessageAction, PlayWaits, planWait } from "./playwait";
import { recentErrorIds } from "./monitoring";
import { currentStageContext } from "./stage/actions";
import { stageVideo } from "./media";
import { reviewToolContext } from "./notes";
import { get, say, set, toast } from "./store";
import { applyUi } from "./ui";
import { flickerDirector } from "./directors";
import { fromDirectorTool } from "./changes";
import type { EndReason } from "./callend";

export type StartOpts = {
  textOnly?: boolean;
  /** v2: connect with overrides.tts.voiceId (the saved voice from the session wins when this is empty) */
  voiceId?: string;
  /** set by the automatic voice switch: the new voice opens with a short line and carries on */
  switched?: boolean;
};

export type DirectorControls = {
  start: (opts?: StartOpts) => Promise<void>;
  /** END-CALL: end the call; `reason` says which path ended it (an End tap when left out). The mic is let go. */
  stop: (reason?: EndReason) => void;
  /** end the session without counting as the user hanging up (no auto-reconnect); resolves once it's closed */
  end: () => Promise<void>;
  /** typed message; starts a text-only session if none is open */
  send: (text: string) => Promise<void>;
  /** tell the Director what the user just did by tapping, so voice and touch stay in step */
  context: (text: string) => void;
  connected: () => boolean;
  /** whether the open session is text-only (typed), so a voice switch reconnects the same way */
  textOnly: () => boolean;
  /** 0..1 levels for the iris animation */
  levels: () => { input: number; output: number };
  /** v2.1: the user tapped play while the Director talks: stop it mid-sentence (the clip wins) */
  interrupt: () => void;
  /**
   * v4 (F3): an "[app] …" line sent as a user message, so the Director answers it (hidden from the transcript).
   * Call tellDirector(), which only sends it when the Director isn't speaking.
   */
  message: (text: string) => void;
  /** v4: "the user is still here" (sendUserActivity): keeps a turn timeout quiet during a play_and_wait */
  activity: () => void;
};
/** What a Director implementation installs (the v4 additions are optional: the defaults below do nothing). */
export type InstallControls = Omit<DirectorControls, "message" | "activity"> & Partial<Pick<DirectorControls, "message" | "activity">>;

// Until the bridge is installed, start/send wait for it instead of doing nothing.
let resolveReady: () => void = () => {};
const ready = new Promise<void>((r) => (resolveReady = r));

/** The active controls (the bridge or the mock Director replace them via installDirector). */
export const director: DirectorControls = {
  start: async (opts) => {
    set({ director: "connecting", directorError: null });
    await ready;
    return director.start(opts);
  },
  stop: () => {},
  end: async () => {},
  send: async (text) => {
    await ready;
    return director.send(text);
  },
  context: () => {},
  connected: () => false,
  textOnly: () => false,
  levels: () => ({ input: 0, output: 0 }),
  interrupt: () => {},
  message: () => {},
  activity: () => {},
};
/**
 * 2026-11-03 (James's report): pause the call. It ends (nothing is billed while paused) and the conversation is kept
 * (every end saves the carryover), so the next tap picks up where it left off. The screen says "Paused".
 */
export function pauseCall() {
  if (!director.connected()) return;
  director.stop("pause");
}

/** The Director's own pause (pause_call): let its short line finish first, at most a few seconds. */
export async function pauseAfterLine(maxMs = 6000) {
  const until = Date.now() + maxMs;
  await new Promise((r) => setTimeout(r, 400));
  while (get().director === "speaking" && Date.now() < until) await new Promise((r) => setTimeout(r, 200));
  pauseCall();
}

export function installDirector(c: InstallControls) {
  Object.assign(director, c);
  resolveReady();
}

/**
 * The `switch_director_voice` directive: post a carryover summary, end the session and reconnect with the new
 * voice, with no tap. Without an open session the voice is just saved for next time.
 */
let switching = false;
export async function switchVoice(voiceId: string, voiceName?: string) {
  if (switching) return;
  switching = true;
  set({ directorSwitching: voiceName ?? "the new voice" });
  try {
    const how = await switchDirectorVoice(
      {
        saveCarryover: (summary) => api.saveCarryover(summary),
        end: () => director.end(),
        start: (o) => director.start(o),
        say: (t) => say("studio", t),
        connected: () => director.connected(),
        textOnly: () => director.textOnly(),
      },
      get().transcript,
      voiceId,
      voiceName,
      get().me?.user.name.split(" ")[0],
    );
    const me = get().me;
    if (me) set({ me: { ...me, user: { ...me.user, directorVoiceId: voiceId, directorVoiceName: voiceName ?? me.user.directorVoiceName } } });
    if (how === "saved") toast(`Saved. ${voiceName ?? "The new voice"} is your Director's voice now.`, "good");
  } finally {
    switching = false;
    set({ directorSwitching: null });
  }
}

/**
 * The product Director's client tools, as product/core's core/director/tools.ts defines them (DIRECTOR_TOOLS).
 * This list only decides which tools take the SDK's normal path; anything else the agent calls is still forwarded
 * (onUnhandledClientToolCall in the bridge), so adding a tool to core needs no web change.
 */
export const KNOWN_TOOLS = [
  "account",
  "what_can_we_make",
  "set_brand",
  "finish_onboarding",
  "new_ad",
  "list_projects",
  "open_project",
  "update_brief",
  "write_script",
  "edit_scene",
  "read_script",
  "estimate",
  "render",
  "render_status",
  "play_video",
  "list_voices",
  "set_narrator",
  "library",
  "use_from_library",
  "save_to_library",
  "credits",
  "buy_credits",
  "go_to",
  // v2 (spec-v2-video.md)
  "whats_happening",
  "make_frame",
  "make_location",
  "make_clip",
  "make_sounds",
  "preview_scene_sound",
  // 2026-10-31: the voices on their own (record, play alone or over the picture) and the lip-sync lead-in
  "record_voices",
  "play_voices",
  "set_lip_lead",
  "move_line",
  "make_music",
  "approve",
  "scratch",
  "set_line_speed",
  "redo",
  "produce",
  "add_product_photo",
  "audition_voices",
  "search_voices",
  "voice_sample",
  "set_director_voice",
  // Key Light Director: become another character or take a new name
  "set_director",
  // v2.1 (spec-v2.1.md)
  "what_models",
  "set_model",
  "show",
  // v3 (spec-v3-formats.md)
  "new_video",
  "set_character",
  "remember",
  "recall",
  "forget",
  // v4 (V4-D5): answered when playback settles (runTool's wait path)
  "play_and_wait",
  // v4 (V4-D5): the stage and the on-screen controls
  "show_on_stage",
  "show_choices",
  "variations",
  "pause",
  "resume",
  "back",
  "fold",
  // 2026-11-03: the Director pauses the call itself ("one sec")
  "pause_call",
  // 2026-11-03: read a website for a video
  "read_website",
  // v4 (D7): the fallback tools; (V4-SP): spending and limits
  "how_do_i",
  "report_problem",
  "send_feedback",
  "spending",
  "set_limit",
  // v4 (V4-U1): the change history and undo
  "list_changes",
  "undo_change",
  // v4 (T5): reviewing a finished video (play_moment and compare answer when playback settles, like play_and_wait)
  "find_moment",
  "play_moment",
  "add_note",
  "list_notes",
  "plan_note",
  "apply_note",
  "edit_note",
  "compare",
  "critique",
  "safe_zones",
  "listen",
  // v4 (D1-D4, SH, MS, D6 parity): every other tool in core, so all of them take the SDK's normal path
  "receipts",
  "email_receipt",
  "export_my_data",
  "delete_my_data",
  "add_files",
  "retag",
  "move_item",
  "suno_pack",
  "change_length",
  "reorder_scenes",
  "split_scene",
  "merge_scenes",
  "delete_scene",
  "add_scene",
  "rewrite_script",
  "make_character_sheet",
  "approve_sheet",
  "redo_sheet",
  "show_sheet",
  "older_looks",
  "update_older_looks",
  "list_collections",
  "new_collection",
  "update_collection",
  "move_video",
  "make_series",
  "use_collection_update",
  "model_advice",
  "set_model_rules",
  "show_showcase",
  "restore_version",
  "cancel_job",
  "retry_job",
  "duplicate_video",
  "show_references",
  "set_references",
  "use_last_frame",
  "set_title_card",
  "check_text",
  "set_transitions",
  "set_captions",
  "share_link",
  "revoke_link",
  "restyle",
  // v4 (V4-MC): compare models on a scene
  "compare_models",
  "use_comparison",
  // lip sync (2026-10-30)
  "set_lip_sync",
  "rate_lip_sync",
  "lip_sync_notes",
  // 2026-11-03: the agent's merged tools (core merged.ts); the old names above stay for taps
  "memory",
  "my_data",
  "changes",
  "share",
  "feedback",
  "playback",
  "job",
  "lip_sync",
  "scenes",
  "collection",
  "sheet",
  "library_item",
  "notes",
];

/** What the iris says while a tool runs. */
export const DOING: Record<string, string> = {
  // 2026-11-03: the agent's merged tools
  memory: "Checking my notes",
  my_data: "Getting your data ready",
  changes: "Looking at the changes",
  share: "Sorting out the link",
  feedback: "Passing it to the team",
  playback: "Got it",
  job: "Checking on that",
  lip_sync: "Setting the lip sync",
  scenes: "Changing the scenes",
  collection: "Working on the collection",
  sheet: "Working on the sheet",
  library_item: "Sorting the library",
  notes: "Going through the notes",
  account: "Checking your account",
  what_can_we_make: "Pulling up what we can make",
  set_brand: "Saving your brand",
  new_ad: "Starting a new ad",
  new_video: "Starting a new video",
  create_project: "Starting a new video",
  open_project: "Opening the project",
  set_character: "Updating the cast",
  remember: "Making a note",
  recall: "Checking my notes",
  forget: "Forgetting that",
  update_brief: "Updating the brief",
  write_script: "Writing the script",
  edit_scene: "Editing the scene",
  read_script: "Reading the script",
  estimate: "Pricing it up",
  render: "Starting the render",
  render_status: "Checking the render",
  play_video: "Cueing the video",
  list_voices: "Finding narrators",
  set_narrator: "Setting the narrator",
  library: "Looking in your library",
  use_from_library: "Fetching from your library",
  save_to_library: "Saving to your library",
  credits: "Checking your credits",
  buy_credits: "Opening checkout",
  whats_happening: "Checking on the work",
  make_frame: "Starting a frame",
  make_location: "Drawing the place",
  make_clip: "Starting a clip",
  set_lip_sync: "Setting lip sync",
  rate_lip_sync: "Noting that",
  lip_sync_notes: "Checking the lip-sync ratings",
  make_sounds: "Listening for sounds",
  preview_scene_sound: "Mixing the sounds over the scene",
  record_voices: "Pricing the voices",
  play_voices: "Lining up the voices",
  set_lip_lead: "Moving the line",
  move_line: "Moving the line",
  make_music: "Starting the music",
  approve: "Approving",
  scratch: "Scratching that take",
  set_line_speed: "Changing the timing",
  redo: "Starting a redo",
  produce: "Lining up the work",
  add_product_photo: "Adding the photo",
  audition_voices: "Finding voices",
  search_voices: "Searching the voice library",
  voice_sample: "Recording a sample",
  set_director_voice: "Changing my voice",
  set_director: "Changing who I am",
  what_models: "Checking the models",
  set_model: "Switching the model",
  show: "Cueing it up",
  play_and_wait: "Cueing it up",
  show_on_stage: "Putting it on screen",
  show_choices: "Laying out the options",
  variations: "Trying some options",
  pause: "Pausing",
  pause_call: "Pausing the call",
  read_website: "Reading the website",
  resume: "Playing",
  back: "Going back",
  fold: "Opening that",
  how_do_i: "Looking that up",
  report_problem: "Telling support",
  send_feedback: "Passing it on",
  spending: "Adding up your spending",
  set_limit: "Changing your limit",
  list_changes: "Looking at what changed",
  undo_change: "Putting it back",
  find_moment: "Finding that moment",
  play_moment: "Cueing it up",
  add_note: "Making a note",
  list_notes: "Reading the notes",
  plan_note: "Working out the fix",
  apply_note: "Making the fix",
  edit_note: "Updating the note",
  compare: "Making a quick preview",
  critique: "Watching it closely",
  safe_zones: "Checking what's covered",
  listen: "Setting up listening mode",
  // v4 (D1-D4, SH, MS): the rest, so the iris never shows a raw tool name (parity.test checks every tool has one)
  finish_onboarding: "Noting that down",
  go_to: "Opening that",
  list_projects: "Looking at your videos",
  receipts: "Finding your receipts",
  email_receipt: "Sending the receipt",
  export_my_data: "Sending your data",
  delete_my_data: "Sending the confirmation",
  add_files: "Opening the upload",
  retag: "Tagging it",
  move_item: "Moving it",
  suno_pack: "Writing the Suno pack",
  change_length: "Changing the length",
  reorder_scenes: "Moving the scenes",
  split_scene: "Splitting the scene",
  merge_scenes: "Joining the scenes",
  delete_scene: "Removing the scene",
  add_scene: "Adding a scene",
  rewrite_script: "Rewriting the script",
  restyle: "Pricing the new look",
  make_character_sheet: "Starting the sheet",
  approve_sheet: "Approving the sheet",
  redo_sheet: "Redoing the sheet",
  show_sheet: "Pulling up the sheet",
  older_looks: "Finding older looks",
  update_older_looks: "Updating older looks",
  list_collections: "Looking at your collections",
  new_collection: "Making the collection",
  update_collection: "Saving the collection",
  move_video: "Moving the video",
  make_series: "Making a series",
  use_collection_update: "Bringing it in",
  model_advice: "Weighing up the models",
  set_model_rules: "Saving the model rules",
  show_showcase: "Lining up the showcase",
  restore_version: "Restoring that version",
  cancel_job: "Stopping it",
  retry_job: "Trying again",
  duplicate_video: "Making a copy",
  show_references: "Pulling up the references",
  set_references: "Changing the references",
  use_last_frame: "Grabbing the clip's last frame",
  set_title_card: "Designing the title, then checking it reads",
  check_text: "Checking the text reads over the picture",
  set_transitions: "Changing the transitions",
  set_captions: "Changing the captions",
  share_link: "Making a link",
  revoke_link: "Turning the link off",
  compare_models: "Lining up the models", // v4 (V4-MC)
  use_comparison: "Using that one",
};
export const doingLabel = (name: string) => DOING[name] ?? name.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/** The ad open on screen, if any (sent with every tool call and the session). */
export const openProjectId = () => location.pathname.match(/^\/p\/([^/]+)/)?.[1] ?? null;

/** v4: the play_and_wait calls waiting on playback (one at a time; src/playwait.ts). */
export const playWaits = new PlayWaits({
  now: () => Date.now(),
  setTimer: (f, ms) => setTimeout(f, ms),
  clearTimer: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  activity: () => {
    if (director.connected()) director.activity();
  },
});

let tokenN = 0;
const makeToken = () => `pw_${Date.now().toString(36)}_${++tokenN}`;

function reviewContextNow(): { review?: ReturnType<typeof reviewToolContext> } {
  if (typeof location === "undefined") return {};
  let t: number | null = null;
  try {
    const v = stageVideo();
    if (v.getAttribute("src")) t = v.currentTime || 0;
  } catch {
    /* no media element */
  }
  const r = reviewToolContext(location.pathname, location.search, t);
  return r ? { review: r } : {};
}

/** What every tool call carries about the screen (spec-v4 section 8.4; the API ignores what it doesn't know). */
export const toolContext = (name?: string) => ({
  // 2026-11-03 (merged tools): the agent's own calls; their results name tools as the agent calls them
  agent: true,
  projectId: openProjectId(),
  stage: currentStageContext(),
  // v4 (T5): on the review screen, the version and the playhead (add_note pins "here")
  ...reviewContextNow(),
  // v4 (D7): a problem report also carries the screen and this tab's recent error ids (no screenshot: see report_problem)
  ...(name === "report_problem" || name === "feedback" ? { report: { path: location.pathname, sentryEventIds: recentErrorIds() } } : {}),
});

/**
 * 2026-10-31 (production: "play_moment with layers=voice failed four times with 'Tool execution was abandoned due to
 * user input', and the user heard no audio"): the voice agent abandons a client tool that's still waiting when the user
 * speaks. These tools play something, so they answer the Director quickly and play by themselves: never a blocking
 * wait (a `wait` play is turned into a plain one: it starts on its own and the app says when it ends), and if the
 * server is still making it after FAST_ANSWER_MS the Director hears "it'll start by itself" and the play is applied
 * when it arrives (a failure is passed on to the Director as an app message).
 */
export const PLAY_FAST = new Set(["play_moment", "preview_scene_sound", "play_voices"]);
export const FAST_ANSWER_MS = 2500;
export const STILL_MAKING = "It's being made and will start playing by itself in a few seconds. Say that in a few words, then stay quiet while it plays; the app tells you when it ends.";

/** A ui directive (or list) with every blocking play turned into one that starts by itself and asks after. */
export function unblockPlays<U>(ui: U): U {
  const fix = (o: unknown): unknown => {
    const d = o as { type?: string; wait?: boolean; token?: string; then?: string } | null;
    if (!d || d.type !== "play" || !d.wait) return o;
    const { token: _t, ...rest } = d;
    return { ...rest, wait: false, then: d.then ?? "ask" };
  };
  return (Array.isArray(ui) ? ui.map(fix) : fix(ui)) as U;
}

const hasPlay = (ui: unknown) => (Array.isArray(ui) ? ui : [ui]).some((o) => (o as { type?: string } | null)?.type === "play");

async function runFastTool(name: string, params: Record<string, unknown>): Promise<string> {
  let late = false;
  const call = fromDirectorTool(api.directorTool(name, params ?? {}, toolContext(name))).then(
    (r) => {
      void applyUi(unblockPlays(r.ui));
      // answered after the Director moved on: a play just starts (the Director was told it would); anything else
      // (it couldn't be made, nothing recorded yet) is passed on so the Director can say it
      if (late) {
        if (hasPlay(r.ui)) director.context(`[app] ${r.result}`);
        else tellDirector(`[app] ${r.result}`);
      }
      return r.result;
    },
    (e: Error) => {
      const text = `The studio couldn't do that: ${e.message}`;
      if (late) tellDirector(`[app] ${text}`);
      return text;
    },
  );
  const quick = await Promise.race([call, new Promise<null>((r) => setTimeout(() => r(null), FAST_ANSWER_MS))]);
  if (quick !== null) return quick;
  late = true;
  return STILL_MAKING;
}

/**
 * 2026-11-04 (Keaton's report: "each write_script attempt was abandoned with 'Tool execution was abandoned due to
 * user input' whenever the user spoke, so the script never got written after three or four tries"): writing a script
 * is one long model call, and the voice agent drops a client tool that's still waiting when the user talks. These
 * tools answer the Director within FAST_ANSWER_MS and keep working in the background; the result reaches the
 * Director as an app message when it lands, so it can keep talking meanwhile. A second call for the same video while
 * one is still writing joins it instead of starting (and paying for) another.
 */
export const WORK_IN_BACKGROUND = new Set(["write_script", "rewrite_script", "read_website"]);
const BG_STARTED: Record<string, string> = {
  write_script: "It's writing the script now, which takes about half a minute. Tell them that in a few words and keep talking with them meanwhile (don't call write_script again); the app tells you when it's ready.",
  rewrite_script: "It's rewriting the script now, which takes about half a minute. Tell them that in a few words and keep talking with them meanwhile (don't call it again); the app tells you when it's ready.",
  read_website: "It's reading the website now. Tell them in a few words and keep talking; the app tells you when it's done.",
};
const STILL_WRITING = "That's still being worked on from before; the app tells you when it's ready. Don't call it again.";
const inBackground = new Map<string, Promise<string>>();

async function runBackgroundTool(name: string, params: Record<string, unknown>): Promise<string> {
  const ctx = toolContext(name);
  // one key per video for both script tools (the server also writes one at a time: studio.ts writeScript)
  const key = `${name === "rewrite_script" ? "write_script" : name}:${(params?.project_id as string | undefined) ?? (ctx as { projectId?: string } | undefined)?.projectId ?? ""}`;
  if (inBackground.has(key)) return STILL_WRITING;
  let late = false;
  const call = fromDirectorTool(api.directorTool(name, params ?? {}, ctx)).then(
    (r) => {
      void applyUi(r.ui);
      if (late) tellDirector(`[app] Done (${name.replace(/_/g, " ")}): ${r.result}`);
      return r.result;
    },
    (e: Error) => {
      const text = `The studio couldn't do that: ${e.message}`;
      if (late) tellDirector(`[app] ${text}`);
      return text;
    },
  ).finally(() => inBackground.delete(key));
  inBackground.set(key, call);
  // `late` is set by the timer itself, so a result landing right at the deadline is still passed on
  const quick = await Promise.race([call, new Promise<null>((r) => setTimeout(() => { late = true; r(null); }, FAST_ANSWER_MS))]);
  if (quick !== null) return quick;
  return BG_STARTED[name] ?? "It's working on that now; the app tells you when it's done.";
}

/** Forward one tool call to the API and apply its `ui`. Shared by the real and the mock Director. */
export async function runTool(name: string, params: Record<string, unknown>): Promise<string> {
  set({ director: "thinking", directorDoing: doingLabel(name) });
  // 2026-11-04: a rough cut's first build takes about half a minute, so play_and_wait on it answers fast and plays by itself
  const roughCut = name === "play_and_wait" && /rough[\s-]*cut/i.test(String(params?.item ?? ""));
  if (PLAY_FAST.has(name) || WORK_IN_BACKGROUND.has(name) || roughCut) {
    flickerDirector();
    try {
      return await (WORK_IN_BACKGROUND.has(name) ? runBackgroundTool(name, params) : runFastTool(name, params));
    } finally {
      set((s) => ({ directorDoing: null, director: s.director === "thinking" ? "listening" : s.director }));
    }
  }
  // the Director's light gives a quick warm flicker as it starts on something
  flickerDirector();
  let waitFor: Promise<string> | null = null;
  try {
    const r = await fromDirectorTool(api.directorTool(name, params ?? {}, toolContext(name))); // X3: no change toast on top of the Director
    // play_and_wait: begin waiting before the play starts, so an instant outcome (blocked) can't be missed
    const plan = planWait(r.ui, makeToken);
    if (plan.wait) waitFor = playWaits.begin(plan.wait);
    void applyUi(plan.ui);
    if (!waitFor) return r.result;
  } catch (e) {
    return `The studio couldn't do that: ${(e as Error).message}`;
  } finally {
    set((s) => ({ directorDoing: null, director: s.director === "thinking" ? "listening" : s.director }));
  }
  // the agent hears nothing until playback ends, is stopped, blocked or replaced (or the guard runs out)
  return waitFor ?? "";
}

/**
 * v4 (finding F3): tell the Director something it should answer ("[app] finished playing …", "[app] The user picked
 * B by tapping …") as a user message. Held while a play_and_wait is pending and while the Director is speaking; if it
 * keeps talking past APP_HOLD_MAX_MS it goes as a contextual update instead of cutting it off.
 */
export function tellDirector(text: string) {
  const began = Date.now();
  let speakingSince: number | null = null;
  let quietSince = get().director === "speaking" ? Date.now() : Date.now() - 10_000;
  let wasSpeaking = get().director === "speaking";
  const step = () => {
    if (!director.connected()) return;
    const now = Date.now();
    const speaking = get().director === "speaking";
    if (speaking && speakingSince === null) speakingSince = now;
    if (!speaking && wasSpeaking) quietSince = now;
    wasSpeaking = speaking;
    const waiting = !!playWaits.pending();
    const a = appMessageAction({ waiting, speaking, quietMs: speaking ? 0 : now - quietSince, heldMs: speakingSince === null ? 0 : now - Math.max(speakingSince, began) });
    if (a === "send") director.message(text);
    else if (a === "context") director.context(text);
    else setTimeout(step, 200);
  };
  step();
}
